import React, { useEffect, useRef, useState } from 'react'
import { useStores } from '../../../stores/store'
import { CSVLink } from 'react-csv';
import Moment from 'moment';
import Popup from 'reactjs-popup'
import { Link } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { Table } from '../../common/table';
import { Pagination } from '../../common/pagination';
import { useTranslation } from 'react-i18next';
import Loader from '../../common/loader';

const LS_KEY = 'admin_school_activation_codes_pageSize';

function AdminSchoolActivationCodesPage() {
  const { t } = useTranslation();
  const { auth } = useStores();

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isDataError, setIsDataError] = useState(false);
  const [codes, setCodes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? +saved : 50;
  });
  const [resetCodeId, setResetCodeId] = useState<number>(0);

  const [filterValue, setFilterValue] = useState<string>("free");
  const searchKey = useRef<string>("");
  const searchRef = useRef(null);

  useEffect(() => {
    getCodes();
  }, [])

  const getCodes = (page = 1, size = pageSize, filter?: string) => {
    setIsDataLoading(true);
    setIsDataError(false);

    const status = filter !== undefined ? filter : filterValue;
    auth.getSchoolActivationCodes(status, searchKey.current, page, size).then((res) => {
      const data = res.data;
      const isPaginated = data && !Array.isArray(data) && Array.isArray(data.results);
      setCodes(isPaginated ? data.results : (Array.isArray(data) ? data : []));
      setTotalCount(isPaginated ? (data.count || 0) : (Array.isArray(data) ? data.length : 0));
      setCurrentPage(page);
      setIsDataLoading(false);
    }).catch((error) => {
      setIsDataLoading(false);
      setIsDataError(true);
    });
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    localStorage.setItem(LS_KEY, String(size));
    getCodes(1, size);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t('schoolActivationCodes'),
        columns: [
          {
            Header: t('modify'),
            accessor: "id",
            Cell: ({ cell }) => (
              <Link to={`/admin/school-activation-codes/${cell.row.values.id}`}>
                <FaEdit />
              </Link>
            )
          },
          {
            Header: t('code'),
            accessor: 'code',
          },
          {
            Header: t('generationDate'),
            accessor: "generation_date",
            Cell: ({ cell }) => (
              Moment(cell.row.values.generation_date).format('DD/MM/yyyy')
            )
          },
          {
            Header: t('activationDate'),
            accessor: "activation_date",
            Cell: ({ cell }) => (
              cell.row.values.activation_date ? Moment(cell.row.values.activation_date).format('DD/MM/yyyy') : t('notActivated')
            )
          },
          {
            Header: t('school'),
            accessor: "school_name",
            Cell: props => {
              if (props.value == null || props.value.length == 0) {
                return "-";
              } else {
                return props.value
              }
            }
          },
          {
            Header: t('productionLot'),
            accessor: "production_lot",
          },
          {
            Header: t('reset'),
            Cell: props => (
              props.row.values.activation_date ? <button className='border-2 border-red-500 hover:border-red-700 text-red-500 hover:text-red-700 font-bold py-1 px-3 rounded-full' onClick={() => setResetCodeId(props.row.values.id)}>Reset</button> : <span></span>
            )
          },
        ],
      },
    ],
    []
  );

  const handleFilterChange = e => {
    const newFilter = e.target.value;
    setFilterValue(newFilter);
    getCodes(1, pageSize, newFilter);
  }

  const search = event => {
    if (event.target.value && event.target.value.length < 4)
      return;

    searchKey.current = event.target.value;
    getCodes(1);
  }

  const handleSubmitReset = async (e) => {
    e.preventDefault();
    await auth.resetSchoolActivationCode(resetCodeId);
    setResetCodeId(0);
    getCodes(currentPage);
  }

  const paginationControls = (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={pageSize}
      onPageChange={(page) => getCodes(page)}
      onPageSizeChange={handlePageSizeChange}
    />
  );

  return (
    <div className="w-full pb-12">
      <Popup open={resetCodeId != 0}
        position="right center"
        closeOnDocumentClick
        onClose={() => setResetCodeId(0)}>
        <form onSubmit={handleSubmitReset} className="w-full flex flex-col items-center gap-6 m-4">
          {t('confirmResetActivation')}
          <div className="w-full flex flex-row justify-center gap-6">
            <button className="bg-primary text-white font-bold py-2 px-4 rounded-full" type="submit">{t('yes')}</button>
            <button onClick={(e) => { e.preventDefault(); setResetCodeId(0) }} className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full">{t('no')}</button>
          </div>
        </form>
      </Popup>

      <div className="flex flex-col">
        <div className="flex flex-row justify-start">
          <span className="text-4xl">{t('schoolActivationCodes')}</span>
        </div>
        <div className='flex flex-row mt-4 justify-between'>
          <div className='flex flex-row mt-4 space-x-2'>
            <select className="bg-primary text-white rounded-full p-2 cursor-pointer" value={filterValue} onChange={handleFilterChange}>
              <option value="free">{t('free')}</option>
              <option value="used">{t('used')}</option>
            </select>
            <input onChange={search} ref={searchRef} placeholder={t('searchMin4Chars')} className="shadow appearance-none border rounded w-full p-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
            <CSVLink className="bg-primary hover:bg-blue-700 text-white font-bold p-4 rounded-full" data={codes} filename={`school_activation_codes_${Moment().format("DD-MM-YYYY")}.csv`}>
              {t('export')}
            </CSVLink>
          </div>
          <Link to={'/admin/school-activation-codes/generate'} className='bg-primary flex flex-row items-center p-4 rounded-full font-bold text-white'>
            {t('generateCodes')}
          </Link>
        </div>

        {!isDataError && !isDataLoading && <div className="mt-4">{paginationControls}</div>}

        {isDataLoading && <div className='grid h-screen place-items-center'><Loader /></div>}

        {isDataError && <div className='grid h-screen place-items-center'>{t('cannotLoadPage')}</div>}

        {!isDataError && !isDataLoading && <Table columns={columns} data={codes} />}

        {!isDataError && !isDataLoading && <div className="mt-4">{paginationControls}</div>}
      </div>
    </div>
  )
}

export default AdminSchoolActivationCodesPage
